import React from 'react';

import { ErrorOutlineOutlined as InfoIcon } from '@mui/icons-material';
import { Alert, AlertTitle, Button, Typography } from '@mui/material';

import type { Nullable } from '@clearverify/types';

import {
  useVerificationRequestPaymentInfoQuery,
  VerificationRequestStatus,
  VerificationRequestStripePaymentStatus,
} from 'src/api';
import { ModalId } from 'src/constants/modals';
import { useScreenInfo } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';
import { useVerificationRequest } from 'src/hooks/verificationRequest';
import { VerificationRequestPaymentTryAgainModalArgs } from 'src/modals/verificationRequest';
import type { SxProp } from 'src/types/mui';

interface InboxItemContentVerificationRequestPaymentStatusProps {
  inboxItemId: Nullable<string>;
}

export const InboxItemContentVerificationRequestPaymentStatus: React.FC<
  InboxItemContentVerificationRequestPaymentStatusProps
> = ({ inboxItemId }) => {
  const { isMobile } = useScreenInfo();
  const { openModal } = useModal();

  const { verificationRequest } = useVerificationRequest({ inboxItemId });
  const { data: verificationRequestPaymentInfo } = useVerificationRequestPaymentInfoQuery({
    variables: {
      where: { inboxItemId },
    },
    skip: !inboxItemId,
  });

  const handleTryAgainClick = React.useCallback(() => {
    if (!inboxItemId) return;
    openModal(ModalId.VerificationRequestPaymentTryAgainModal, {
      inboxItemId,
    } satisfies VerificationRequestPaymentTryAgainModalArgs);
  }, [inboxItemId, openModal]);

  const stripePaymentStatus = React.useMemo(
    () => verificationRequestPaymentInfo?.verificationRequest?.stripePaymentStatus,
    [verificationRequestPaymentInfo?.verificationRequest?.stripePaymentStatus],
  );

  const verificationRequestStatus = React.useMemo(
    () => verificationRequest?.status,
    [verificationRequest?.status],
  );

  const canTryAgainPayment = React.useMemo(
    () =>
      stripePaymentStatus === VerificationRequestStripePaymentStatus.Canceled &&
      verificationRequestStatus !== VerificationRequestStatus.DeclinedByEmployee &&
      verificationRequestStatus !== VerificationRequestStatus.VerifierToRespond,
    [stripePaymentStatus, verificationRequestStatus],
  );

  const alertSeverity = React.useMemo(() => {
    switch (stripePaymentStatus) {
      case VerificationRequestStripePaymentStatus.Canceled:
        return 'error';
      default:
        return 'warning';
    }
  }, [stripePaymentStatus]);

  const textPaymentInfo = React.useMemo(() => {
    if (
      stripePaymentStatus &&
      stripePaymentStatus === VerificationRequestStripePaymentStatus.Canceled
    ) {
      return (
        <Typography variant="body2">
          Your request has not been processed due to an inability to confirm payment. Please update
          your payment options under your payment settings or contact your account admin.
        </Typography>
      );
    }

    return (
      <Typography variant="body2">
        Payments are usually processed within one business day. If payments are delayed for more
        than one business day, please check with your financial institution. You can always contact
        us from the &quot;Resources & Help&quot; section in the bottom left corner of the page with
        any additional questions.
      </Typography>
    );
  }, [stripePaymentStatus]);

  if (
    !(
      [
        VerificationRequestStripePaymentStatus.Canceled,
        VerificationRequestStripePaymentStatus.Processing,
      ] as Nullable<VerificationRequestStripePaymentStatus>[]
    ).includes(stripePaymentStatus)
  ) {
    return null;
  }

  return (
    <Alert
      severity={alertSeverity}
      sx={alertStyles(alertSeverity)}
      icon={<InfoIcon />}
      action={
        canTryAgainPayment ? (
          <Button
            color={alertSeverity}
            onClick={handleTryAgainClick}
            sx={{ whiteSpace: 'nowrap', p: isMobile ? 0 : 'auto' }}
          >
            Try again
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>
        Payment&nbsp;
        {stripePaymentStatus === VerificationRequestStripePaymentStatus.Canceled
          ? 'Failure'
          : 'Pending'}
      </AlertTitle>
      {textPaymentInfo}
    </Alert>
  );
};

const alertStyles =
  (alertSeverity: 'error' | 'warning'): SxProp =>
  theme => ({
    backgroundColor: 'transparent',
    border: 1,
    borderColor: theme.palette[alertSeverity].main,
  });
